/**
 * CallDock (CALLS-00) — the live-call stack on the right edge of EVERY screen
 * (locked decision #1). Ringing card → click → the caller's 360 in the working
 * window; the board is never hijacked (two-window rule). Multiple simultaneous
 * calls stack newest-on-top, max 3 visible + "+N more".
 *
 * Spec: research/calls-screenpop-design.md §3/§3b (ui-ux-pro-max pass):
 * states never color-only (icon + label + accent), tabular timers, 200ms/140ms
 * transform-opacity motion honoring prefers-reduced-motion, aria-live polite
 * announcements that never steal focus.
 *
 * Visibility = `calls.dock` capability (everyone by default; per-user deny in
 * Users & Roles; Dispatcher locked on — locked decision #3).
 *
 * Until the Road-2 phone adapter is approved, the only source is the
 * admin-only simulator at the bottom of the dock.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { PhoneCall, PhoneIncoming, PhoneOff, PhoneForwarded, Phone, Mic, MicOff, Pause, Play, ChevronDown, ChevronUp, FlaskConical, X, Loader2, PenLine, Pencil, Check } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCan, usePermissions } from "@/lib/permissions/PermissionProvider";
import { softphone, useSoftphone, type XferTarget } from "@/features/calls/softphone";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { displayCustomerName } from "@/lib/format";
import { useCurrentUserEmail } from "@/features/inbox/api";
import { useAddSignal } from "@/features/customer-360/api";
import {
  formatPhone,
  useSetCallTranscript,
  useSetDispatcherNotes,
  useClaimCall,
  useEndCall,
  useLiveCalls,
  useSimAdvance,
  useSimRing,
  type CallEvent,
} from "@/features/calls/calls";
import { Sparkles } from "lucide-react"; // (separate line: lucide tree-shakes fine)

const MAX_VISIBLE = 3;

function useNowTick(active: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

function secondsSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}
function mmss(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function CallDock() {
  const canSee = useCan("calls.dock");
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const myEmail = useCurrentUserEmail().data ?? null;
  const { data } = useLiveCalls();
  const advance = useSimAdvance();
  const simRing = useSimRing();
  const claim = useClaimCall();
  const endCall = useEndCall();
  const [expanded, setExpanded] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  // UserMenu → "Simulate incoming call" (admin) opens the phone form here.
  useEffect(() => {
    const onOpen = () => setSimOpen(true);
    window.addEventListener("pegasus-call-sim-open", onOpen);
    return () => window.removeEventListener("pegasus-call-sim-open", onOpen);
  }, []);
  const [simPhone, setSimPhone] = useState("");
  // CALLS-AI-02: the sim's stand-in for a microphone — SAVING the words is all
  // it takes; the DB trigger runs the AI by itself (same path as a real
  // recording landing). Results stream back onto the card via realtime.
  const saveWords = useSetCallTranscript();
  const [aiOpenId, setAiOpenId] = useState<string | null>(null);
  const [aiText, setAiText] = useState("");
  // CALLS-NOTES-01 (Seth 2026-10-01): the dispatcher's own notes, typed right
  // on the card while they talk. They ride into the dispatch when the visit
  // is booked from this call (human words lead, the AI summary follows).
  const saveNotes = useSetDispatcherNotes();
  const [notesOpenId, setNotesOpenId] = useState<string | null>(null);
  const [notesText, setNotesText] = useState("");
  // CALL-ROUTE-01: property facts the AI heard get a one-tap file to the
  // customer's Watch For — a HUMAN tap is the only thing that writes.
  const addSignal = useAddSignal();
  const [factsSaved, setFactsSaved] = useState<string[]>([]);
  // PHONE-SOFT-01: the browser phone. When this login has the capability and
  // turned it on, Telnyx rings THIS TAB alongside the desk phones; Answer /
  // Decline live right on the ringing card, the in-call console pins on top.
  // (The on/off switch itself lives in the HEADER — DOCK-TIDY-01.)
  const canSoftphone = useCan("calls.softphone");
  const soft = useSoftphone(canSoftphone);
  // DOCK-TIDY-01 (Seth: cards "can potentially get in the way"): a call
  // you're already on collapses to a slim pill — expand only when you want
  // the full card (AI band, facts, sim controls).
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Slice 2: transfer popover state (targets load when it opens).
  const [xferOpen, setXferOpen] = useState(false);
  const [xferTargets, setXferTargets] = useState<{ dispatchers: XferTarget[]; techs: XferTarget[] } | null>(null);
  const [xferBusy, setXferBusy] = useState<string | null>(null);
  const [xferErr, setXferErr] = useState<string | null>(null);

  useRealtimeRefresh(["call_event"], [["live_calls"]]);

  const calls = data ?? [];
  useNowTick(calls.length > 0 || !!soft.active);
  const visible = expanded ? calls : calls.slice(0, MAX_VISIBLE);
  const hiddenCount = calls.length - visible.length;

  // Screen-reader announcement for the newest ringing call (polite — no focus steal).
  const newestRinging = calls.find((c) => c.state === "ringing");
  const announce = useMemo(() => {
    if (!newestRinging) return "";
    const who = newestRinging.customer_fullname
      ? displayCustomerName(null, newestRinging.customer_fullname)
      : formatPhone(newestRinging.from_e164);
    return `Incoming call: ${who}`;
  }, [newestRinging?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canSee) return null;
  // DOCK-TIDY-01: with the on/off switch in the header, an idle dock renders
  // NOTHING — it only exists while a call does (or a browser call survives
  // its dock row).
  if (calls.length === 0 && !isAdmin && !soft.active && !soft.ringing) return null;

  const open = (c: CallEvent) => {
    if (c.custno) navigate(`/customers/${c.custno}`);
    else navigate(`/customers?q=${encodeURIComponent(formatPhone(c.from_e164))}`);
  };

  return (
    <div
      className="pointer-events-none fixed right-4 top-20 z-[60] flex w-[296px] flex-col gap-2"
      aria-label="Live calls"
    >
      <span aria-live="polite" className="sr-only">
        {announce}
      </span>

      {/* PHONE-SOFT-01/2 — the in-call console, pinned on top while YOU are
          on a browser call: caller · timer · mute · hold · transfer · notes ·
          hang up. The call's dock row hides while this owns it (DOCK-TIDY). */}
      {soft.active && (() => {
        const row = calls.find((c) => softphone.matches(c.from_e164, soft.active)) ?? null;
        const who = row?.customer_fullname
          ? displayCustomerName(null, row.customer_fullname)
          : formatPhone(soft.active.from || null);
        const ctl = "flex h-8 w-8 items-center justify-center rounded-lg transition-colors";
        return (
          <div className="pointer-events-auto overflow-hidden rounded-xl border-l-[3px] border-emerald-600 bg-slate-900 text-white shadow-e3">
            <div className="flex items-center gap-2 px-3 py-2">
              <Phone className={cn("h-4 w-4 shrink-0", soft.held ? "text-amber-400" : "text-emerald-400")} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{who}</div>
                <div className="text-[11px] text-slate-300">
                  {soft.held ? "On hold — they hear music" : "In your browser"} · {mmss(soft.activeSince ? Math.floor((Date.now() - soft.activeSince) / 1000) : 0)}
                </div>
              </div>
              <button type="button" onClick={() => softphone.toggleMute()}
                title={soft.muted ? "Unmute your microphone" : "Mute your microphone"}
                className={cn(ctl, soft.muted ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-white/10 text-white hover:bg-white/20")}>
                {soft.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => void softphone.toggleHold()}
                title={soft.held ? "Take them off hold" : "Put the caller on hold (they hear music)"}
                className={cn(ctl, soft.held ? "bg-amber-500 text-white hover:bg-amber-400" : "bg-white/10 text-white hover:bg-white/20")}>
                {soft.held ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
              {row && (
                <button type="button"
                  onClick={() => {
                    setXferErr(null);
                    setXferOpen((v) => !v);
                    if (!xferTargets) {
                      softphone.fetchTargets().then(setXferTargets).catch((e) => setXferErr(String((e as Error)?.message ?? e)));
                    }
                  }}
                  title="Send this call to someone else (they take over; you drop off)"
                  className={cn(ctl, xferOpen ? "bg-white/25 text-white" : "bg-white/10 text-white hover:bg-white/20")}>
                  <PhoneForwarded className="h-4 w-4" />
                </button>
              )}
              {row && (
                <button type="button"
                  onClick={() => { setNotesOpenId(notesOpenId === row.id ? null : row.id); setNotesText(row.dispatcher_notes ?? ""); }}
                  title="Notes while you talk — they ride into the dispatch"
                  className={cn(ctl, "bg-white/10 text-white hover:bg-white/20", row.dispatcher_notes && "text-sky-300")}>
                  <PenLine className="h-4 w-4" />
                </button>
              )}
              <button type="button" onClick={() => softphone.hangup()} title="Hang up"
                className={cn(ctl, "bg-rose-600 text-white hover:bg-rose-500")}>
                <PhoneOff className="h-4 w-4" />
              </button>
            </div>
            {/* Transfer picker — front desk first, then browsers, then cells. */}
            {xferOpen && (
              <div className="border-t border-white/10 bg-white px-2 py-2 text-slate-800">
                <div className="px-1 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Send this call to…</div>
                <div className="max-h-44 space-y-0.5 overflow-y-auto">
                  {([{ kind: "pstn", value: "+19528819000", label: "Front desk (main line)" } as XferTarget,
                    ...(xferTargets?.dispatchers ?? []),
                    ...(xferTargets?.techs ?? [])]).map((t) => (
                    <button key={`${t.kind}:${t.value}`} type="button" disabled={!!xferBusy}
                      onClick={() => {
                        if (!row) return;
                        setXferBusy(t.value); setXferErr(null);
                        softphone.transfer(row.id, t)
                          .then(() => { setXferOpen(false); setXferBusy(null); })
                          .catch((e) => { setXferBusy(null); setXferErr(String((e as Error)?.message ?? e)); });
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-700 transition-colors hover:bg-brand-50 disabled:opacity-50">
                      {xferBusy === t.value ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-600" /> : <PhoneForwarded className="h-3.5 w-3.5 text-slate-400" />}
                      <span className="min-w-0 flex-1 truncate">{t.label}</span>
                    </button>
                  ))}
                  {!xferTargets && !xferErr && (
                    <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> loading people…</div>
                  )}
                </div>
                {xferErr && <div className="px-1 pt-1 text-[11px] text-rose-600">{xferErr}</div>}
                <div className="px-1 pt-1 text-[10px] text-slate-400">If they don't pick up, the call falls back to the desk phones — the caller is never stranded.</div>
              </div>
            )}
            {/* Notes while on the browser call — same notes, same dispatch ride. */}
            {row && notesOpenId === row.id && (
              <div className="border-t border-white/10 bg-white px-2.5 py-2">
                <textarea value={notesText} onChange={(e) => setNotesText(e.target.value)} autoFocus rows={3}
                  placeholder="What they're telling you — rides into the dispatch when you book…"
                  className="w-full rounded-md border border-hairline p-1.5 text-[11px] text-slate-800 focus:border-sky-400 focus:outline-none" />
                <div className="mt-1 flex items-center gap-2">
                  <button type="button" disabled={saveNotes.isPending}
                    onClick={() => saveNotes.mutate({ id: row.id, notes: notesText.trim() || null }, { onSuccess: () => { setNotesOpenId(null); setNotesText(""); } })}
                    className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:bg-sky-300">
                    {saveNotes.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save notes
                  </button>
                  <button type="button" onClick={() => { setNotesOpenId(null); setNotesText(""); }}
                    className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Rare timing gap: the browser is ringing but the dock row hasn't
          landed yet — still give Answer/Decline so the call is never lost. */}
      {soft.ringing && !calls.some((c) => c.state === "ringing" && softphone.matches(c.from_e164, soft.ringing)) && (
        <div className="pointer-events-auto rounded-xl border-l-[3px] border-emerald-500 bg-white px-3 py-2.5 shadow-e3">
          <div className="flex items-center gap-2">
            <PhoneIncoming className="h-4 w-4 shrink-0 animate-pulse text-emerald-600" />
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">
              {formatPhone(soft.ringing.from || null)}
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button type="button" onClick={() => softphone.answer()}
              className="flex-1 rounded-md bg-emerald-600 px-2 py-1.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700">
              Answer
            </button>
            <button type="button" onClick={() => softphone.decline()}
              className="rounded-md bg-slate-100 px-2 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-200">
              Decline
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {visible.map((c) => {
          const ringing = c.state === "ringing";
          const who = c.customer_fullname
            ? displayCustomerName(null, c.customer_fullname)
            : formatPhone(c.from_e164);
          const secs = ringing
            ? secondsSince(c.started_at)
            : c.answered_at
              ? secondsSince(c.answered_at)
              : 0;
          const mine = !!c.answered_by && c.answered_by === myEmail;
          const othersName = c.answered_by ? c.answered_by.split("@")[0] : null;

          // DOCK-TIDY-01: while the browser console (pinned above) owns this
          // call, its dock row stays out of the way entirely.
          if (!ringing && soft.active && softphone.matches(c.from_e164, soft.active)) {
            return null;
          }

          // DOCK-TIDY-01 (Seth: "the 'you're on the call' window can get in
          // the way"): a call YOU answered (or a sim call) collapses to a
          // slim pill — body opens the 360, pencil = notes, chevron expands
          // the full card (AI band, facts, sim controls).
          if (!ringing && c.state === "answered" && (mine || c.provider === "sim") && expandedId !== c.id) {
            return (
              <motion.div key={c.id} layout={!reduceMotion}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.14 }}
                className="pointer-events-auto">
                <div className="flex items-center gap-1.5 rounded-full border border-brand-200 bg-white/95 py-1 pl-2.5 pr-1 shadow-e2 backdrop-blur">
                  <PhoneCall className="h-3.5 w-3.5 shrink-0 text-brand-600" />
                  <button type="button" onClick={() => open(c)}
                    title="You're on this call — open the customer"
                    className="min-w-0 flex-1 truncate text-left text-xs text-slate-700 hover:text-slate-900">
                    <span className="font-semibold text-slate-900">{who}</span>
                    <span className="ml-1.5 tabular-nums text-slate-400">{mmss(secs)}</span>
                  </button>
                  <button type="button"
                    onClick={() => { setNotesOpenId(notesOpenId === c.id ? null : c.id); setNotesText(c.dispatcher_notes ?? ""); }}
                    title="Notes while you talk — they ride into the dispatch"
                    className={cn("rounded-full p-1 transition-colors hover:bg-sky-50", c.dispatcher_notes ? "text-sky-600" : "text-slate-300 hover:text-sky-600")}>
                    <PenLine className="h-3.5 w-3.5" />
                  </button>
                  {c.provider === "sim" && isAdmin && (
                    <button type="button" onClick={() => advance.mutate({ call: c, to: "completed" })}
                      title="Hang up (simulated)"
                      className="rounded-full p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600">
                      <PhoneOff className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button type="button" onClick={() => setExpandedId(c.id)}
                    title="Show the full call card (AI notes, details)"
                    className="rounded-full p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600">
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                {notesOpenId === c.id && (
                  <div className="mt-1 rounded-xl border border-hairline bg-white px-2.5 py-2 shadow-e2">
                    <textarea value={notesText} onChange={(e) => setNotesText(e.target.value)} autoFocus rows={3}
                      placeholder="What they're telling you — rides into the dispatch when you book…"
                      className="w-full rounded-md border border-hairline p-1.5 text-[11px] focus:border-sky-400 focus:outline-none" />
                    <div className="mt-1 flex items-center gap-2">
                      <button type="button" disabled={saveNotes.isPending}
                        onClick={() => saveNotes.mutate({ id: c.id, notes: notesText.trim() || null }, { onSuccess: () => { setNotesOpenId(null); setNotesText(""); } })}
                        className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:bg-sky-300">
                        {saveNotes.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save notes
                      </button>
                      <button type="button" onClick={() => { setNotesOpenId(null); setNotesText(""); }}
                        className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Cancel</button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          }

          // Answered by ANOTHER dispatcher → a slim ghost pill: it doesn't
          // disappear (awareness), it just stops shouting (no noise). Still
          // clickable to open the 360.
          if (!ringing && c.answered_by && !mine) {
            return (
              <motion.div
                key={c.id}
                layout={!reduceMotion}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                className="pointer-events-auto"
              >
                <button
                  type="button"
                  onClick={() => open(c)}
                  className="flex w-full items-center gap-2 rounded-full border border-hairline bg-white/85 px-3 py-1.5 text-left opacity-80 shadow-e1 backdrop-blur transition-opacity hover:opacity-100"
                >
                  <PhoneCall className="h-3.5 w-3.5 shrink-0 text-brand-600" />
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
                    <span className="font-semibold text-slate-800">{othersName}</span>{" "}
                    on the call · {who}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                    {mmss(secs)}
                  </span>
                </button>
              </motion.div>
            );
          }
          return (
            <motion.div
              key={c.id}
              layout={!reduceMotion}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 16 }}
              transition={{ duration: ringing ? 0.2 : 0.14, ease: [0.25, 1, 0.5, 1] }}
              className={cn(
                "pointer-events-auto relative overflow-hidden rounded-xl border-l-[3px] bg-white shadow-e3",
                ringing ? "border-emerald-500" : "border-brand-600",
              )}
            >
              {/* Manual clear (Seth 2026-06-25) — a real call can always be
                  dismissed by hand if a carrier hangup callback is ever missed,
                  so a card never gets stuck "active" with no way to clear it.
                  Sim rows use their own Hang-up control instead. */}
              {c.provider !== "sim" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    endCall.mutate(c);
                  }}
                  title="Clear this call from the dock"
                  aria-label="Clear call"
                  className="absolute right-1 top-1 z-10 rounded-full p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              {/* DOCK-TIDY-01: a call you expanded can tuck back into its pill. */}
              {!ringing && c.state === "answered" && (mine || c.provider === "sim") && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setExpandedId(null); }}
                  title="Tuck this call back into its pill"
                  aria-label="Collapse call card"
                  className={cn(
                    "absolute top-1 z-10 rounded-full p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600",
                    c.provider !== "sim" ? "right-7" : "right-1",
                  )}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => open(c)}
                className="block w-full px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
              >
                <div className="flex items-center gap-2">
                  {ringing ? (
                    <PhoneIncoming className="h-4 w-4 shrink-0 animate-pulse text-emerald-600" />
                  ) : (
                    <PhoneCall className="h-4 w-4 shrink-0 text-brand-700" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">
                    {who}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">
                    {mmss(secs)}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 pl-6 text-[11px]">
                  <span
                    className={cn(
                      "rounded px-1 py-0.5 font-semibold uppercase tracking-wide",
                      ringing
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-brand-50 text-brand-700",
                    )}
                  >
                    {ringing ? "Ringing" : mine ? "You're on the call" : "On the phone"}
                  </span>
                  {c.has_agreement && (
                    <span className="rounded bg-emerald-50 px-1 py-0.5 font-semibold uppercase tracking-wide text-emerald-700">
                      Plan member
                    </span>
                  )}
                  {c.match_type === "confirmed" ? (
                    <span className="truncate text-slate-500">
                      {c.city ?? formatPhone(c.from_e164)}
                    </span>
                  ) : (
                    <span className="rounded bg-amber-50 px-1 py-0.5 font-semibold uppercase tracking-wide text-amber-700">
                      no match — new caller
                    </span>
                  )}
                </div>
                {/* Context before answering: the matched caller's history line */}
                {c.match_type === "confirmed" && (
                  <div className="mt-1 truncate pl-6 text-[11px] text-slate-500">
                    {c.last_visit
                      ? `Last visit ${new Date(c.last_visit + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                      : "No visits on record"}
                    {" · "}
                    {formatPhone(c.from_e164)}
                  </div>
                )}
              </button>
              {/* PHONE-SOFT-01 — THIS browser is ringing for THIS call:
                  Answer connects right here (and screen-pops the 360);
                  Decline only drops OUR leg — the desk phones keep ringing. */}
              {ringing && c.provider !== "sim" && soft.ringing && softphone.matches(c.from_e164, soft.ringing) && (
                <div className="flex items-center gap-1.5 border-t border-emerald-100 bg-emerald-50/70 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={async () => {
                      softphone.answer();
                      if (!c.answered_by) {
                        try { await claim.mutateAsync(c); } catch { /* webhook stamps it anyway */ }
                      }
                      open(c);
                    }}
                    className="flex-1 rounded-md bg-emerald-600 px-2 py-1 text-[12px] font-bold text-white transition-colors hover:bg-emerald-700"
                  >
                    Answer in browser
                  </button>
                  <button
                    type="button"
                    onClick={() => softphone.decline()}
                    title="Stop ringing this browser — the desk phones keep ringing"
                    className="rounded-md bg-white px-2 py-1 text-[12px] font-semibold text-slate-500 shadow-sm transition-colors hover:bg-slate-100"
                  >
                    Decline
                  </button>
                </div>
              )}
              {/* CALLS-CREATE-01 (Seth 2026-08-28): an unmatched caller can
                  become a customer in one tap — the number rides straight
                  into the Add Customer phone field. Card click still goes to
                  search (eyeball near-matches / other spellings first). */}
              {c.match_type !== "confirmed" && (c.from_e164 ?? "").replace(/\D/g, "").length >= 10 && (
                <div className="flex items-center border-t border-hairline bg-amber-50/70 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customers?q=${encodeURIComponent(formatPhone(c.from_e164))}&new=1`);
                    }}
                    title="Open Add Customer with this caller's number already filled in"
                    className="rounded-md bg-brand-700 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-brand-800"
                  >
                    Start a new customer
                  </button>
                  <span className="ml-auto text-[11px] text-slate-400">number carries over</span>
                </div>
              )}
              {/* REAL calls: Telnyx can't see which desk phone picked up, so
                  the dispatcher who answered CLAIMS the call — that stamps
                  answered_by and screen-pops the customer on THEIR window
                  only (everyone else's card fades to the ghost pill). Also
                  the manual fix if the carrier's answered event is missed. */}
              {c.provider !== "sim" && !c.answered_by && (
                <div className="flex items-center gap-1.5 border-t border-hairline bg-slate-50 px-2 py-1.5">
                  <button
                    type="button"
                    disabled={claim.isPending}
                    onClick={async () => {
                      await claim.mutateAsync(c);
                      open(c);
                    }}
                    title="You picked this call up on your phone — put your name on it and pull up the customer"
                    className="rounded-md bg-brand-700 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
                  >
                    That&apos;s me — I answered
                  </button>
                  <span className="ml-auto text-[11px] text-slate-400">
                    {formatPhone(c.to_e164)}
                  </span>
                </div>
              )}
              {/* CALLS-NOTES-01 — the dispatcher's notes, right where the call
                  lives. Saved notes show as their own band; the editor opens
                  in place; booking from this call carries them into the
                  dispatch note automatically. */}
              {notesOpenId === c.id ? (
                <div className="border-t border-hairline bg-white px-2.5 py-2">
                  <textarea
                    value={notesText}
                    onChange={(e) => setNotesText(e.target.value)}
                    autoFocus
                    rows={3}
                    placeholder="What they're telling you — rides into the dispatch when you book…"
                    className="w-full rounded-md border border-hairline p-1.5 text-[11px] focus:border-sky-400 focus:outline-none"
                  />
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={saveNotes.isPending}
                      onClick={() =>
                        saveNotes.mutate(
                          { id: c.id, notes: notesText.trim() || null },
                          { onSuccess: () => { setNotesOpenId(null); setNotesText(""); } },
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:bg-sky-300"
                    >
                      {saveNotes.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Save notes
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNotesOpenId(null); setNotesText(""); }}
                      className="text-[11px] font-medium text-slate-400 hover:text-slate-600"
                    >
                      Cancel
                    </button>
                    {saveNotes.isError && <span className="text-[11px] text-rose-600">{String((saveNotes.error as Error)?.message ?? "failed")}</span>}
                  </div>
                </div>
              ) : c.dispatcher_notes ? (
                <div className="group/notes flex items-start gap-1.5 border-t border-hairline bg-sky-50/60 px-3 py-1.5 text-[11px] text-slate-600">
                  <PenLine className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" />
                  <span className="min-w-0 flex-1 whitespace-pre-wrap">{c.dispatcher_notes}</span>
                  <button
                    type="button"
                    onClick={() => { setNotesOpenId(c.id); setNotesText(c.dispatcher_notes ?? ""); }}
                    title="Edit your notes"
                    className="mt-0.5 shrink-0 rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:text-sky-700 focus-visible:opacity-100 group-hover/notes:opacity-100"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
              ) : !ringing ? (
                <button
                  type="button"
                  onClick={() => { setNotesOpenId(c.id); setNotesText(""); }}
                  className="flex w-full items-center gap-1.5 border-t border-hairline px-3 py-1.5 text-left text-[11px] font-medium text-slate-400 transition-colors hover:bg-sky-50/50 hover:text-sky-700"
                >
                  <PenLine className="h-3 w-3" /> Notes while you talk — they ride into the dispatch
                </button>
              ) : null}
              {/* CALLS-AI-02: what the AI heard, right on the card — appears on
                  its own once the run finishes (no button on real calls). */}
              {c.ai_summary ? (
                <div className="flex items-start gap-1.5 border-t border-hairline bg-violet-50/60 px-3 py-1.5 text-[11px] text-slate-600">
                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-violet-500" />
                  <span className="min-w-0">{c.ai_summary}</span>
                </div>
              ) : c.ai_status === "pending" ? (
                <div className="flex items-center gap-1.5 border-t border-hairline bg-violet-50/40 px-3 py-1.5 text-[11px] text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin text-violet-400" /> AI is listening to the call…
                </div>
              ) : null}
              {/* CALL-ROUTE-01 — durable PROPERTY facts the AI heard, each
                  with one tap to file it on the customer's Watch For. Job
                  details and one-offs ride the dispatch note instead; nothing
                  here saves without the dispatcher's tap. */}
              {(() => {
                const propFacts = (c.ai_extract?.facts ?? []).filter((f) => f.kind === "property" && f.text);
                if (propFacts.length === 0 || !c.custno) return null;
                return (
                  <div className="space-y-1 border-t border-hairline bg-amber-50/50 px-3 py-1.5">
                    {propFacts.map((f, i) => {
                      const k = `${c.id}:${i}`;
                      const done = factsSaved.includes(k);
                      return (
                        <div key={k} className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-900">
                          <span className="min-w-0 flex-1">{f.text}</span>
                          {done ? (
                            <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-bold text-emerald-700">
                              <Check className="h-3 w-3" /> on Watch For
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={addSignal.isPending}
                              onClick={() =>
                                addSignal.mutate(
                                  { custno: c.custno!, locno: c.locno ?? null, signal_type: f.slot || "heads_up", value: f.text, detail: { source: "call", call_id: c.id } },
                                  { onSuccess: () => setFactsSaved((s) => [...s, k]) },
                                )
                              }
                              title="Save to this customer's Watch For — dispatchers and techs see it on every future visit"
                              className="shrink-0 rounded-md bg-amber-600/90 px-1.5 py-0.5 text-[10px] font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                            >
                              + Watch For
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Simulator controls — sim rows + admins only */}
              {c.provider === "sim" && isAdmin && (
                <div className="flex gap-1 border-t border-hairline bg-slate-50 px-2 py-1.5">
                  {ringing && (
                    <>
                      <SimBtn
                        label="Answer"
                        onClick={async () => {
                          // SCREEN-POP: answering opens the caller's 360 in
                          // YOUR working window (only the answerer pops —
                          // everyone else sees the ghost pill).
                          await advance.mutateAsync({ call: c, to: "answered" });
                          if (c.custno) navigate(`/customers/${c.custno}`);
                        }}
                      />
                      <SimBtn label="Miss it" onClick={() => advance.mutate({ call: c, to: "missed" })} />
                    </>
                  )}
                  {c.state === "answered" && (
                    <>
                      <SimBtn label="Hang up" onClick={() => advance.mutate({ call: c, to: "completed" })} />
                      <SimBtn label="AI notes" onClick={() => { setAiOpenId(aiOpenId === c.id ? null : c.id); setAiText(""); }} />
                    </>
                  )}
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400">
                    <FlaskConical className="h-3 w-3" /> simulated
                  </span>
                </div>
              )}
              {/* CALLS-AI-02 sim path: the textarea stands in for the phone's
                  microphone. SAVING the words is the whole action — the DB
                  trigger runs the AI on its own, exactly like a real
                  recording landing, and the notes appear on the card above. */}
              {c.provider === "sim" && isAdmin && aiOpenId === c.id && c.state === "answered" && (
                <div className="border-t border-hairline bg-white px-2.5 py-2">
                  <textarea
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    rows={3}
                    placeholder="Type or paste what was said — stands in for the call audio…"
                    className="w-full rounded-md border border-hairline p-1.5 text-[11px] focus:border-brand-400 focus:outline-none"
                  />
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={saveWords.isPending || aiText.trim().length < 10}
                      onClick={() =>
                        saveWords.mutate(
                          { call_id: c.id, transcript: aiText.trim() },
                          { onSuccess: () => { setAiOpenId(null); setAiText(""); } },
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:bg-violet-300"
                    >
                      {saveWords.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Save the call words
                    </button>
                    <span className="text-[10px] text-slate-400">the AI runs by itself from here</span>
                    {saveWords.isError && <span className="text-[11px] text-rose-600">{String((saveWords.error as Error)?.message ?? "failed")}</span>}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* A failed write must never be silent (missed-ticket/log insert). */}
      {(advance.isError || simRing.isError || claim.isError) && (
        <div className="pointer-events-auto rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 shadow-e1">
          {String(
            (advance.error as Error)?.message ??
              (simRing.error as Error)?.message ??
              (claim.error as Error)?.message ??
              "Call action failed",
          )}
        </div>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="pointer-events-auto rounded-full bg-slate-900/85 px-3 py-1 text-center text-xs font-semibold text-white shadow-e2 backdrop-blur transition-colors hover:bg-slate-900"
        >
          +{hiddenCount} more ringing
        </button>
      )}
      {expanded && calls.length > MAX_VISIBLE && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="pointer-events-auto text-center text-[11px] text-slate-500 hover:text-slate-700"
        >
          collapse
        </button>
      )}

      {/* Admin test-ring (the CALLS-00 simulated source; removed when the
          Road-2 adapter goes live). The trigger lives in the user menu
          ("Simulate incoming call") so nothing floats over page toolbars
          (HD-SWEEP: the chip collided with the Inbox / PO actions); only the
          phone form pops here, and only while it's open. */}
      {isAdmin && simOpen && (
        <div className="pointer-events-auto self-end">
          {simOpen ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (simPhone.replace(/\D/g, "").length >= 10) {
                  simRing.mutate(simPhone);
                  setSimPhone("");
                  setSimOpen(false);
                }
              }}
              className="flex items-center gap-1 rounded-lg border border-hairline bg-white p-1 shadow-e2"
            >
              <input
                autoFocus
                value={simPhone}
                onChange={(e) => setSimPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSimOpen(false)}
                placeholder="Phone to simulate…"
                aria-label="Phone number to simulate an incoming call from"
                className="w-44 rounded px-2 py-1 text-xs focus:outline-none"
              />
              <button
                type="submit"
                className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-800"
              >
                Ring
              </button>
            </form>
          ) : null}
        </div>
      )}

      {/* DOCK-TIDY-01: the browser-phone on/off switch moved to the HEADER
          (SoftphoneChip) — nothing idle floats over page content any more.
          The dock only speaks when a call does. */}
    </div>
  );
}

function SimBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100"
    >
      {label}
    </button>
  );
}
